import { eventManager } from "@foretagsplatsen/yaem";
import { max } from "@foretagsplatsen/core-helpers/array";
import numbro from "../numbroWrapper.js";
import numbroWithFailsafe from "../numbroWithFailsafe.js";

eventManager.at("period").register("change", (period) => {
	const currencyName = period.getYear().getCurrencyCultureName();
	if (currencyName) {
		changeCulture(currencyName);
	}
});

/**
 * Change the current formatting locale.
 *
 * @param {string} cultureCode - The locale as a BCP 47 string
 */
export function changeCulture(cultureCode) {
	// Default fallback to finnish / EUR
	numbro.setLanguage(cultureCode, "fi-FI");
}

/**
 * Return the most sensible formatter to format all numbers in a list.
 */
export function findBestFormatForNumbers(numbers) {
	const biggest = max(numbers);
	if (biggest >= 10_000_000) {
		return formatMillionWithTwoDecimals;
	}
	if (biggest >= 1_000_000) {
		return formatThousandNoDecimals;
	}

	return formatFullNoDecimals;
}

/**
 * Format `number` as millions to a string with a thousand separator and two decimal.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatMillionWithTwoDecimals(number) {
	return formatCurrency(number, "millionWithTwoDecimals");
}

/**
 * Format `number` as thousands to a string with a thousand separator and no decimal.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatThousandNoDecimals(number) {
	return formatCurrency(number, "thousandNoDecimals");
}

/**
 * Unlike `millionWithTwoDecimals`, `fullWithTwoDecimals` always shows the 2
 * decimals, even when for integers, since the amount is not averaged and
 * there is no abbreviation.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatFullWithTwoDecimals(number) {
	return formatCurrency(number, "fullWithTwoDecimals");
}

/**
 * Format `number` to a string with a thousand separator and no decimal.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatFullNoDecimals(number) {
	return formatCurrency(number, "fullNoDecimals");
}

/**
 * Format `number` to a string with a thousand separator and two decimal and no
 * currency symbol.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatFullWithTwoDecimalsNoCurrency(number) {
	return format(number, "fullWithTwoDecimalsNoCurrency");
}

/**
 * Format `number` to a string with a thousand separator and no decimal and no
 * currency symbol.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatFullWithNoDecimalsNorCurrency(number) {
	return format(number, "fullWithNoDecimalsNorCurrency");
}

/**
 * Creates a number formatter with the specified number of decimals.
 */
export function withNumberOfDecimals(numberOfDecimals) {
	return (number) =>
		numbroWithFailsafe(number).format({
			mantissa: numberOfDecimals,
			output: "currency",
			characteristic: 1,
			optionalCharacteristic: false,
			average: true,
			optionalMantissa: true,
			trimMantissa: false,
			thousandSeparated: true,
			spaceSeparated: true,
			spaceSeparatedCurrency: true,
		});
}

/**
 * Return the `cultureData` for `cultureCode`, or the current culture if
 * none is provided.
 */
function getCultureData(cultureCode) {
	return numbro.languageData(cultureCode);
}

/**
 * Return the currency symbol for `cultureCode`, or the current culture if
 * none is provided.
 */
export function getCurrencySymbol(cultureCode) {
	return getCultureData(cultureCode).currency.symbol;
}

/**
 * Format `number` to a string with a thousand separator and no decimal and a
 * currency symbol.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatFullWithNoDecimals(number) {
	return formatCurrency(number, "fullWithNoDecimals");
}

/**
 * Return the list of supported currencies.
 *
 * @return {Array} The list of supported currencies
 */
export function getCurrencies() {
	return [
		{
			iso4217: "EUR",
			symbol: "€",
			locales: ["fi-FI", "nl-NL", "de-DE"],
		},
		{ iso4217: "GBP", symbol: "£", locales: ["en-GB"] },
		{ iso4217: "USD", symbol: "$", locales: ["en-US"] },
		{ iso4217: "SEK", symbol: "kr", locales: ["sv-SE"] },
		{ iso4217: "DKK", symbol: "kr", locales: ["da-DK"] },
		{ iso4217: "NOK", symbol: "kr", locales: ["nb-NO"] },
	];
}

function formatForCurrentCulture(formatName) {
	const data = getCultureData();

	const defaultFormat = {
		mantissa: 2,
		trimMantissa: true,
		average: true,
	};
	return (
		(data && data.formats && data.formats[formatName]) || {
			...defaultFormat,
			...data.currencyFormat,
		} ||
		null
	);
}

/**
 * Format `number` to a string with a thousand separator and two decimal and a
 * currency symbol.
 *
 * @param {number} number - The number to format
 * @param {string} formatName - The name of the format to use
 * @return {string}
 */
export function formatCurrency(number, formatName) {
	const cultureFormat = formatForCurrentCulture(formatName);
	return numbroWithFailsafe(number).formatCurrency(cultureFormat);
}

function format(number, formatName) {
	return numbroWithFailsafe(number).format(
		formatForCurrentCulture(formatName),
	);
}
